import React, { useState, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalContent, ModalHeader, ModalBody, Input, Select, SelectItem } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { AgGridReact } from 'ag-grid-react';
import type {
  ColDef,
  GridApi,
  ICellRendererParams,
  IsFullWidthRowParams,
  RowClickedEvent,
  RowHeightParams,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { CtyButton, CtySpinner, ConvertToLocalDate, useTheme } from '@circuitry-ai/doc-ui';
import type {
  BatchSummaryItem,
  SchedulerHistoryBatchItem,
  SchedulerBatchDetailResponse,
} from 'modules/shared/data/src/features/settings/settingstypes';
import { useGetSchedulerBatchDetailQuery, useGetSchedulerHistoryQuery } from '@circuitry-ai/doc-data';
import { useNavigate } from 'react-router-dom';

const PER_PAGE_OPTIONS = [10, 20, 40, 50];
const BATCH_ROW_HEIGHT = 48;
/** Scrollable cap for inner claims grid; extra rows scroll inside this area. */
/** Until ResizeObserver reports, parent full-width row uses this (avoids layout jump to 0). */
const DETAIL_ROW_FALLBACK_HEIGHT = 120;
/**
 * Upper bound for measured detail panel height (tabs + padding + claims area).
 * Inner claims list uses max-height + overflow; this caps the outer Ag Grid full-width row.
 */
const DETAIL_PANEL_MAX_HEIGHT = 560;

type ActivityGridRow =
  | ({ kind: 'batch' } & SchedulerHistoryBatchItem)
  | { kind: 'detail'; batchId: string };

function isBatchRow(row: ActivityGridRow | undefined): row is { kind: 'batch' } & SchedulerHistoryBatchItem {
  return row?.kind === 'batch';
}

function formatBatchRunDate(value: string): string {
  if (!value) return '-';
  try {
    return ConvertToLocalDate(value);
  } catch {
    return value;
  }
}

interface ActivityLogGridContext {
  readonly expandedBatchId: string | null;
  readonly reportDetailHeight: (batchId: string, height: number) => void;
}

function getStatusColorClass(status: string): string {
  if (status === 'completed') return 'bg-success/10 text-success';
  if (status === 'failed') return 'bg-danger/10 text-danger';
  return 'bg-warning/10 text-warning';
}

function BatchStatusCell(params: ICellRendererParams<ActivityGridRow>) {
  const d = params.data;
  if (!d || !isBatchRow(d)) return null;
  const colorClass = getStatusColorClass(d.status);
  return <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${colorClass}`}>{d.status}</span>;
}

function ExpandChevronCell(params: ICellRendererParams<ActivityGridRow>) {
  const d = params.data;
  const ctx = params.context as ActivityLogGridContext | undefined;
  if (!d || !isBatchRow(d)) return null;
  const open = ctx?.expandedBatchId === d.batch_id;
  return (
    <span className="inline-flex items-center justify-center mt-2">
      <Icon
        icon={open ? 'mdi:chevron-down' : 'mdi:chevron-right'}
        className="h-5 w-5 text-default-500"
      />
    </span>
  );
}

function BatchDetailFullWidthRow(params: ICellRendererParams<ActivityGridRow>) {
  const d = params.data;
  const ctx = params.context as ActivityLogGridContext | undefined;
  if (!d || d.kind !== 'detail') return null;
  return (
    <BatchDetail
      batchId={d.batchId}
      onLayoutHeight={(h) => ctx?.reportDetailHeight(d.batchId, h)}
    />
  );
}

/** Claims table inside batch detail (tabs + Ag Grid). */
function BatchDetailClaimsGrid({
  filteredSummary,
  activeTab,
}: Readonly<{
  filteredSummary: BatchSummaryItem[];
  activeTab: 'received' | 'processed' | 'skipped' | 'retried';
}>) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const claimsGridThemeStyle = useMemo(
    () =>
      ({
        width: '100%',
        '--ag-border-color': '#e5e7eb',
        '--ag-header-background-color': '#f3f4f6',
        '--ag-selected-row-background-color': 'transparent',
        '--ag-range-selection-background-color': 'transparent',
      }) as React.CSSProperties,
    []
  );

  /** Skipped tab: show full reason text with wrapping; row height follows content. */
  const ReasonCellFull = useCallback((p: ICellRendererParams<BatchSummaryItem>) => {
    const reason = p.data?.reason;
    if (reason == null || reason === '') {
      return <span className="text-default-400">—</span>;
    }
    return <span className="block whitespace-pre-wrap break-words py-1 text-sm leading-snug">{reason}</span>;
  }, []);

  const StatusCell = useCallback(
    (p: ICellRendererParams<BatchSummaryItem>) => {
      const processed = p.data?.is_processed;
      return (
        <span
          className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${
            processed ? 'bg-success/10 text-success' : 'bg-default-100 text-default-600'
          }`}
        >
          {processed ? t('Processed') : t('Skipped')}
        </span>
      );
    },
    [t]
  );

  const NavigateCell = useCallback(
    (p: ICellRendererParams<BatchSummaryItem>) => {
      const id = p.data?.aiworker_usage_id;
      if (!id) return null;
      return (
        <button
          type="button"
          className="p-1 text-default-500 hover:text-primary"
          aria-label={t('View details')}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/history?id=${id}`);
          }}
        >
          <Icon icon="mdi:arrow-top-right" className="h-4 w-4" />
        </button>
      );
    },
    [navigate, t]
  );

  const columnDefs = useMemo(() => {
    const cols: ColDef<BatchSummaryItem>[] = [
      {
        colId: 'claimnumber',
        field: 'claimnumber',
        headerName: t('CLAIM #'),
        minWidth: 120,
        flex: 1,
        cellStyle: { fontFamily: 'ui-monospace, monospace', fontSize: '13px' },
      },
      {
        colId: 'status',
        headerName: t('STATUS'),
        sortable: false,
        width: 120,
        maxWidth: 140,
        cellRenderer: StatusCell,
      },
    ];
    if (activeTab === 'skipped') {
      cols.push({
        colId: 'reason',
        headerName: t('REASON'),
        sortable: false,
        flex: 2,
        minWidth: 200,
        wrapText: true,
        autoHeight: true,
        cellRenderer: ReasonCellFull,
        cellStyle: { whiteSpace: 'normal', lineHeight: 1.45 },
      });
    }
    // Skipped claims usually have no history link; empty action column looked broken — omit on Skipped tab.
    if (activeTab !== 'skipped') {
      cols.push({
        colId: 'action',
        headerName: '',
        width: 48,
        maxWidth: 56,
        sortable: false,
        suppressMovable: true,
        cellRenderer: NavigateCell,
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      });
    }
    return cols as ColDef<BatchSummaryItem>[];
  }, [activeTab, NavigateCell, ReasonCellFull, StatusCell, t]);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      suppressHeaderMenuButton: true,
    }),
    []
  );

  if (filteredSummary.length === 0) {
    return (
      <div
        className="flex items-center justify-center overflow-y-auto rounded border border-default-200 bg-content1 py-10 text-sm text-default-500"
      >
        {t('No claims')}
      </div>
    );
  }

  return (
    <div
      className="min-h-0 w-full overflow-y-auto rounded border border-default-200 bg-content1"
    >
      <div className="claims-batch-detail-grid ag-theme-alpine w-full mb-5" style={claimsGridThemeStyle}>
        <AgGridReact<BatchSummaryItem>
          rowData={filteredSummary}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          headerHeight={40}
          suppressCellFocus
          domLayout="autoHeight"
          getRowId={(p) => String(p.data.claimnumber)}
          {...(activeTab === 'skipped' ? {} : { rowHeight: 40 })}
        />
      </div>
    </div>
  );
}

function formatDisplayKey(key: string): string {
  if (!key) return key;
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function renderValue(value: unknown, level = 0): React.ReactNode {
  if (value == null) return <span className="text-default-400">-</span>;

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-default-400">-</span>;
    return (
      <div className="space-y-2 w-full">
        {value.map((item, idx) => {
          const itemKey = (typeof item === 'object' && item !== null && 'id' in item) ? String(item.id) : `item-${idx}`;
          return (
            <div key={itemKey} className="rounded border border-default-200 bg-content1 p-2">
              {typeof item === 'object' && item !== null
                ? renderObjectHierarchy(item as Record<string, unknown>, level + 1)
                : <span className="text-sm text-default-700">{String(item as string | number | boolean)}</span>}
            </div>
          );
        })}
      </div>
    );
  }

  if (typeof value === 'object') {
    return renderObjectHierarchy(value as Record<string, unknown>, level + 1);
  }

  return <span className="text-sm text-default-700 break-all">{String(value as string | number | boolean)}</span>;
}

function renderObjectHierarchy(data: Record<string, unknown>, level = 0): React.ReactNode {
  const entries = Object.entries(data);
  if (entries.length === 0) return <span className="text-default-400">-</span>;

  return (
    <div className={`space-y-1 ${level > 0 ? 'pl-3 border-l-2 border-default-200' : ''}`}>
      {entries.map(([key, value]) => {
        const isNested = value != null && typeof value === 'object';
        return (
          <div key={key} className="py-1">
            <span className="text-xs font-semibold text-default-500 uppercase tracking-wide">
              {formatDisplayKey(key)}
            </span>
            <div className={isNested ? 'mt-1' : 'mt-0.5'}>
              {renderValue(value, level)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function JsonSection({ title, data }: Readonly<{ title: string; data: Record<string, unknown> | undefined }>) {
  const { t } = useTranslation();
  return (
    <div>
      <h4 className="text-sm font-semibold text-default-600 mb-2">{title}</h4>
      {!data || Object.keys(data).length === 0 ? (
        <p className="text-sm text-default-400 italic">{t('No data')}</p>
      ) : (
        <div className="overflow-auto max-h-[280px] rounded border border-default-200 bg-content1 p-3">
          {renderObjectHierarchy(data)}
        </div>
      )}
    </div>
  );
}

function NonAgentBatchDetail({
  batchDetail,
}: Readonly<{ batchDetail: SchedulerBatchDetailResponse }>) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4 mb-8 w-full">
      <JsonSection title={t('Input')} data={batchDetail.input} />
      <JsonSection title={t('Output')} data={batchDetail.output} />
    </div>
  );
}

function BatchDetail({
  batchId,
  onLayoutHeight,
}: Readonly<{ batchId: string; onLayoutHeight?: (height: number) => void }>) {
  const { t } = useTranslation();
  const { data: batchDetail, isFetching } = useGetSchedulerBatchDetailQuery(batchId, {
    skip: !batchId,
  });

  const [activeTab, setActiveTab] = useState<'received' | 'processed' | 'skipped' | 'retried'>('received');
  const measureRef = useRef<HTMLDivElement>(null);

  const isAgent = !batchDetail?.target_type || batchDetail.target_type === 'agent';

  const summary = batchDetail?.batch_summary?.summary ?? [];
  const retrySummary = batchDetail?.batch_summary?.retry_summary ?? [];
  const receivedCount = batchDetail?.no_of_received_claims ?? '0';
  const processedCount = batchDetail?.no_of_processed_claims ?? '0';
  const skippedCount = batchDetail?.no_of_skipped_claims ?? '0';
  const retriedCount = batchDetail?.no_of_retried_claims;

  const filteredSummary = useMemo(() => {
    if (activeTab === 'retried') return retrySummary;
    if (activeTab === 'received') return summary;
    if (activeTab === 'processed') return summary.filter((s: BatchSummaryItem) => s.is_processed);
    return summary.filter((s: BatchSummaryItem) => s.is_skipped);
  }, [summary, retrySummary, activeTab]);

  useLayoutEffect(() => {
    if (!onLayoutHeight) return;
    const el = measureRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const h = Math.ceil(entry.contentRect.height);
      const clamped = Math.min(Math.max(h, 1), DETAIL_PANEL_MAX_HEIGHT);
      onLayoutHeight(clamped);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [onLayoutHeight, batchId, activeTab, isFetching, filteredSummary.length]);

  if (isFetching && !batchDetail) {
    return (
      <div ref={measureRef} className="flex items-center justify-center border-t border-default-200 bg-default-100 p-4 h-[100px]">
        <CtySpinner size="sm" />
        <span className="ml-2 text-default-500">{t('Loading batch details...')}</span>
      </div>
    );
  }

  if (!isAgent && batchDetail) {
    return (
      <div ref={measureRef} className="min-h-0 w-full max-w-full border-t border-default-200 bg-default-100 p-4">
        <NonAgentBatchDetail batchDetail={batchDetail} />
      </div>
    );
  }

  const tabs = [
    { key: 'received' as const, label: t('Received'), count: receivedCount, icon: 'mdi:file-document-outline' },
    { key: 'processed' as const, label: t('Processed'), count: processedCount, icon: 'mdi:check-circle-outline' },
    { key: 'skipped' as const, label: t('Skipped'), count: skippedCount, icon: 'mdi:close-circle-outline' },
    { key: 'retried' as const, label: t('Retried'), count: retriedCount ?? '0', icon: 'mdi:refresh' },
  ];

  return (
    <div
      ref={measureRef}
      className="min-h-0 w-full max-w-full border-t border-default-200 bg-default-100 p-4"
    >
      <div className="mb-3 flex flex-wrap gap-2 border-b border-default-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-default-500 hover:text-foreground'
            }`}
          >
            <Icon icon={tab.icon} className="h-4 w-4" />
            {tab.label} {tab.count}
          </button>
        ))}
      </div>
      <BatchDetailClaimsGrid filteredSummary={filteredSummary} activeTab={activeTab} />
    </div>
  );
}

export interface ActivityLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  schedulerId: string | null;
  scheduleName?: string;
}

export function ActivityLogModal({
  isOpen,
  onClose,
  schedulerId,
}: Readonly<ActivityLogModalProps>) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchBatchId, setSearchBatchId] = useState('');
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  const gridApiRef = useRef<GridApi<ActivityGridRow> | null>(null);
  const detailHeightsRef = useRef<Map<string, number>>(new Map());

  const { data: historyData, isFetching } = useGetSchedulerHistoryQuery(
    { schedulerId: schedulerId ?? '', page, per_page: perPage },
    { skip: !schedulerId || !isOpen }
  );

  const batches = historyData?.data ?? [];
  const totalCount = historyData?.total_count ?? 0;
  const targetType = batches[0]?.target_type ?? 'agent';
  const isAgentType = targetType === 'agent';
  const filteredBatches = useMemo(() => {
    if (!searchBatchId.trim()) return batches;
    const term = searchBatchId.trim().toLowerCase();
    return batches.filter((b) => b.batch_id.toLowerCase().includes(term));
  }, [batches, searchBatchId]);

  const rowData = useMemo((): ActivityGridRow[] => {
    const rows: ActivityGridRow[] = [];
    for (const b of filteredBatches) {
      rows.push({ kind: 'batch', ...b });
      if (expandedBatchId === b.batch_id) {
        rows.push({ kind: 'detail', batchId: b.batch_id });
      }
    }
    return rows;
  }, [filteredBatches, expandedBatchId]);

  const start = totalCount === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, totalCount);

  const handlePerPageChange = useCallback((keys: Iterable<unknown>) => {
    const v = Number(Array.from(keys)[0]);
    if (!Number.isNaN(v)) {
      setPerPage(v);
      setPage(1);
    }
  }, []);

  const toggleExpand = useCallback((batchId: string) => {
    setExpandedBatchId((prev) => {
      if (prev === batchId) {
        detailHeightsRef.current.delete(batchId);
        queueMicrotask(() => gridApiRef.current?.resetRowHeights());
        return null;
      }
      return batchId;
    });
  }, []);

  const reportDetailHeight = useCallback((batchId: string, height: number) => {
    const clamped = Math.min(Math.max(height, 1), DETAIL_PANEL_MAX_HEIGHT);
    const prev = detailHeightsRef.current.get(batchId);
    if (prev === clamped) return;
    detailHeightsRef.current.set(batchId, clamped);
    requestAnimationFrame(() => gridApiRef.current?.resetRowHeights());
  }, []);

  const gridContext = useMemo(
    (): ActivityLogGridContext => ({
      expandedBatchId,
      reportDetailHeight,
    }),
    [expandedBatchId, reportDetailHeight]
  );

  /** Ag Grid needs a definite block height; flex-1 + % inside NextUI Modal often resolves to 0 and shows no rows. */
  const activityGridContainerStyle = useMemo(
    () =>
      ({
        height: 'clamp(320px, 45vh, 620px)',
        width: '100%',
        minHeight: 320,
        '--ag-border-color': '#e5e7eb',
        '--ag-header-background-color': '#f9fafb',
        '--ag-header-foreground-color': '#4b5563',
        '--ag-row-hover-color': 'rgba(0, 0, 0, 0.04)',
        '--ag-selected-row-background-color': 'transparent',
        '--ag-range-selection-background-color': 'transparent',
      }) as React.CSSProperties,
    []
  );

  const columnDefs = useMemo(() => {
    const cols: ColDef<ActivityGridRow>[] = [
      {
        colId: 'expand',
        headerName: '',
        width: 35,
        sortable: false,
        suppressMovable: true,
        lockPosition: 'left' as const,
        cellRenderer: ExpandChevronCell,
        resizable: false,
      },
      {
        colId: 'batch_id',
        headerName: t('BATCH ID'),
        flex: 1.2,
        minWidth: 140,
        valueGetter: (p) => (isBatchRow(p.data) ? p.data.batch_id : ''),
        cellStyle: { fontFamily: 'ui-monospace, monospace', fontSize: '13px' },
      },
    ];

    if (isAgentType) {
      cols.push(
        {
          colId: 'no_of_received_claims',
          headerName: t('RECEIVED CLAIMS'),
          flex: 0.8,
          minWidth: 120,
          valueGetter: (p) => (isBatchRow(p.data) ? p.data.no_of_received_claims : ''),
        },
        {
          colId: 'no_of_processed_claims',
          headerName: t('PROCESSED CLAIMS'),
          flex: 0.8,
          minWidth: 120,
          valueGetter: (p) => (isBatchRow(p.data) ? p.data.no_of_processed_claims : ''),
        },
        {
          colId: 'no_of_skipped_claims',
          headerName: t('SKIPPED CLAIMS'),
          flex: 0.8,
          minWidth: 120,
          valueGetter: (p) => (isBatchRow(p.data) ? p.data.no_of_skipped_claims : ''),
        },
        {
          colId: 'no_of_retried_claims',
          headerName: t('RETRIED CLAIMS'),
          flex: 0.8,
          minWidth: 120,
          valueGetter: (p) => (isBatchRow(p.data) ? p.data.no_of_retried_claims : ''),
        },
      );
    }

    cols.push(
      {
        colId: 'status',
        headerName: t('STATUS'),
        flex: 0.6,
        minWidth: 100,
        valueGetter: (p) => (isBatchRow(p.data) ? p.data.status : ''),
        cellRenderer: BatchStatusCell,
      },
      {
        colId: 'created_at',
        headerName: t('CREATED AT'),
        flex: 1,
        minWidth: 160,
        valueGetter: (p) => (isBatchRow(p.data) ? formatBatchRunDate(p.data.created_at) : ''),
        cellStyle: { color: isDark ? 'var(--cty-text-secondary)' : '#4b5563', fontSize: '13px' },
      },
    );

    return cols;
  }, [t, isAgentType, isDark]);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      suppressHeaderMenuButton: true,
    }),
    []
  );

  const isFullWidthRow = useCallback((params: IsFullWidthRowParams<ActivityGridRow>) => {
    return params.rowNode.data?.kind === 'detail';
  }, []);

  const getRowHeight = useCallback((params: RowHeightParams<ActivityGridRow>) => {
    if (params.data?.kind === 'detail') {
      return detailHeightsRef.current.get(params.data.batchId) ?? DETAIL_ROW_FALLBACK_HEIGHT;
    }
    return BATCH_ROW_HEIGHT;
  }, []);

  const getRowId = useCallback((params: { data: ActivityGridRow }) => {
    const d = params.data;
    if (d.kind === 'batch') return d.batch_id;
    return `detail-${d.batchId}`;
  }, []);

  const onRowClicked = useCallback(
    (e: RowClickedEvent<ActivityGridRow>) => {
      const d = e.data;
      if (!d || d.kind !== 'batch') return;
      toggleExpand(d.batch_id);
    },
    [toggleExpand]
  );

  const getRowStyle = useCallback(
    (params: { data?: ActivityGridRow }) => {
      const d = params.data;
      if (d?.kind === 'batch' && expandedBatchId === d.batch_id) {
        return { backgroundColor: 'rgba(59, 130, 246, 0.06)' };
      }
      return undefined;
    },
    [expandedBatchId]
  );

  const getRowClass = useCallback((params: { data?: ActivityGridRow }) => {
    return params.data?.kind === 'batch' ? 'cursor-pointer' : 'activity-log-detail-row';
  }, []);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalContent className="flex max-h-[90vh] max-w-[1300px] flex-col w-[90vw]">
        <ModalHeader className="flex flex-col gap-1 border-b pb-4">
          <div>
            <h2 className="text-xl font-bold text-foreground">{t('Activity Log')}</h2>
            <p className="mt-0.5 text-sm text-default-500">{t('Recent runs for this schedule')}</p>
          </div>
          <Input
            placeholder={t('Search Batch ID')}
            value={searchBatchId}
            onChange={(e) => setSearchBatchId(e.target.value)}
            startContent={<Icon icon="mdi:magnify" />}
            size="sm"
            className="my-3 max-w-[400px]"
          />
        </ModalHeader>
        <ModalBody className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
          {isFetching ? (
            <div className="flex items-center justify-center py-12">
              <CtySpinner size="md" />
              <span className="ml-2 text-default-500">{t('Loading activity...')}</span>
            </div>
          ) : (
            <>
              <div
                className="activity-log-modal-grid ag-theme-alpine flex min-h-0 flex-col"
                style={activityGridContainerStyle}
              >
                <style>{`
                  .activity-log-modal-grid .ag-row.activity-log-detail-row {
                    cursor: default;
                  }
                `}</style>
                {filteredBatches.length === 0 ? (
                  <div className="flex flex-1 items-center justify-center py-12 text-default-500">{t('No batches found')}</div>
                ) : (
                  <AgGridReact<ActivityGridRow>
                    className="min-h-0 h-full w-full flex-1"
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    context={gridContext}
                    getRowId={getRowId}
                    isFullWidthRow={isFullWidthRow}
                    fullWidthCellRenderer={BatchDetailFullWidthRow}
                    embedFullWidthRows
                    getRowHeight={getRowHeight}
                    getRowStyle={getRowStyle}
                    getRowClass={getRowClass}
                    headerHeight={44}
                    suppressCellFocus
                    domLayout="normal"
                    animateRows
                    onRowClicked={onRowClicked}
                    onGridReady={(e) => {
                      gridApiRef.current = e.api;
                    }}
                    tooltipShowDelay={0}
                  />
                )}
              </div>
              <div className="flex items-center justify-between rounded-b-large border-t bg-default-100 px-4 py-3 text-sm text-default-500">
                <div className="flex items-center gap-2">
                  <span>{t('Show')}</span>
                  <Select
                    selectedKeys={new Set([String(perPage)])}
                    onSelectionChange={handlePerPageChange}
                    size="sm"
                    classNames={{ trigger: 'min-w-[70px] h-8' }}
                    aria-label={t('Per page')}
                    disallowEmptySelection
                    placeholder={t('Per page')}
                  >
                    {PER_PAGE_OPTIONS.map((n) => (
                      <SelectItem key={String(n)} textValue={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </Select>
                  <span className="min-w-fit">{t('per page')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span>
                    {t('Showing')} {start}-{end} {t('of')} {totalCount} {t('entries')}
                  </span>
                  <CtyButton
                    isIconOnly
                    size="sm"
                    variant="flat"
                    onPress={() => setPage((p) => Math.max(1, p - 1))}
                    isDisabled={page <= 1}
                    aria-label={t('Previous page')}
                  >
                    <Icon icon="mdi:chevron-left" />
                  </CtyButton>
                  <CtyButton
                    isIconOnly
                    size="sm"
                    variant="flat"
                    onPress={() => setPage((p) => p + 1)}
                    isDisabled={end >= totalCount}
                    aria-label={t('Next page')}
                  >
                    <Icon icon="mdi:chevron-right" />
                  </CtyButton>
                </div>
              </div>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
